import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, FileImage, Calendar, TrendingUp } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";

const Stat = ({ label, value, icon: Icon, accent = "text-slate-900", testid }) => (
  <Card className="p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className={`font-display text-4xl font-extrabold mt-2 ${accent}`}>{value}</div>
      </div>
      <div className="w-11 h-11 rounded-lg bg-slate-100 grid place-items-center text-slate-600">
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [byLang, setByLang] = useState([]);
  const [topMrs, setTopMrs] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/analytics/summary"),
      api.get("/admin/analytics/daily?days=14"),
      api.get("/admin/analytics/by-language"),
      api.get("/admin/analytics/top-mrs?limit=8"),
    ]).then(([s, d, l, m]) => {
      setSummary(s.data); setDaily(d.data); setByLang(l.data); setTopMrs(m.data);
    });
  }, []);

  return (
    <Shell role="admin">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Control Room</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-brand-ink">Analytics Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Total Posters" value={summary?.total_posters ?? "—"} icon={FileImage} testid="kpi-total-posters" />
        <Stat label="Today" value={summary?.today_posters ?? "—"} icon={Calendar} testid="kpi-today-posters" />
        <Stat label="Last 7 Days" value={summary?.last_7_days_posters ?? "—"} icon={TrendingUp} testid="kpi-week-posters" />
        <Stat label="Active MRs" value={summary?.total_active_mrs ?? "—"} icon={Users} testid="kpi-active-mrs" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Daily Posters</div>
              <h3 className="font-display text-lg font-bold">Last 14 days</h3>
            </div>
          </div>
          <div className="h-72" data-testid="chart-daily">
            <ResponsiveContainer>
              <AreaChart data={daily}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#be1442" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="#be1442" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Area type="monotone" dataKey="count" stroke="#be1442" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">By Language</div>
          <h3 className="font-display text-lg font-bold mb-3">Usage</h3>
          <div className="h-72" data-testid="chart-language">
            <ResponsiveContainer>
              <BarChart data={byLang}>
                <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="language" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="count" fill="#be1442" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Top MRs</div>
        {topMrs.length === 0 ? (
          <div className="text-sm text-slate-400">No data yet — generate some posters first.</div>
        ) : (
          <div className="space-y-2" data-testid="top-mrs-list">
            {topMrs.map((m, i) => (
              <div key={m.user_id} className="flex items-center justify-between py-2 border-b last:border-b-0 border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center text-sm font-bold text-slate-600">{i + 1}</div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{m.name}</div>
                    <div className="text-xs text-slate-500">{m.emp_code}</div>
                  </div>
                </div>
                <div className="font-display text-xl font-bold text-slate-900">{m.count}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </Shell>
  );
}
